import { useEffect, useState } from 'react'
import Layout from '@/components/Layout'
import { fetchWanted, deleteWanted } from '@/lib/api'
import type { WantedOrder } from '@/lib/types'

export default function WantedList() {
  const [wanted, setWanted] = useState<WantedOrder[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [deleting, setDeleting] = useState<string | null>(null)

  async function load() {
    setLoading(true)
    try { setWanted(await fetchWanted()) } catch { /* noop */ }
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function handleDelete(id: string, slug: string) {
    if (!confirm(`Delete wanted order "${slug}"?`)) return
    setDeleting(id)
    try { await deleteWanted(id); await load() } catch { /* noop */ }
    setDeleting(null)
  }

  const zoneNames: Record<string, string> = { sharjah: 'Sharjah', ajman: 'Ajman', 'al-quoz': 'Dubai · Al Quoz' }
  const filtered = wanted.filter(w =>
    !search ||
    w.title.toLowerCase().includes(search.toLowerCase()) ||
    (w.brand || '').toLowerCase().includes(search.toLowerCase()) ||
    (w.part || '').toLowerCase().includes(search.toLowerCase())
  )

  return (
    <Layout title="Wanted Orders"
      action={<a href="/wanted/new" className="inline-flex items-center gap-2 bg-brand-600 text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-brand-700 transition-colors">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14"/></svg>
        Add Wanted
      </a>}
    >
      <div className="max-w-7xl mx-auto space-y-5">
        <div className="relative max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search wanted orders..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-surface-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <p className="text-slate-400 text-sm col-span-full text-center py-10">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-slate-400 text-sm col-span-full text-center py-10">No wanted orders found.</p>
          ) : filtered.map(w => (
            <div key={w.id} className="bg-white rounded-2xl border border-surface-200 shadow-card hover:shadow-card-hover transition-all p-5">
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white text-sm font-bold shadow">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/></svg>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${w.is_active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-500 border-red-200'}`}>
                  {w.is_active ? 'ACTIVE' : 'CLOSED'}
                </span>
              </div>
              <h3 className="font-semibold text-slate-900 text-sm leading-snug">{w.title}</h3>
              <p className="text-xs text-slate-500 mt-1">{w.part || '—'}</p>
              <div className="flex flex-wrap items-center gap-2 mt-3 text-xs">
                {w.zone && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-surface-100 font-medium text-slate-600">{zoneNames[w.zone] ?? w.zone}</span>}
                {w.budget_aed != null && <span className="font-bold text-purple-700">{Number(w.budget_aed).toLocaleString()} AED</span>}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-surface-100">
                <a href={`/wanted/${w.id}`} className="text-xs font-semibold text-brand-600 hover:text-brand-700">Edit</a>
                <button onClick={() => handleDelete(w.id, w.slug)} disabled={deleting === w.id}
                  className="text-xs font-semibold text-red-500 hover:text-red-600 disabled:opacity-50">
                  {deleting === w.id ? '...' : 'Delete'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  )
}
